import React from 'react'
import styled, { css } from 'styled-components'
import { func, bool } from 'prop-types'
import { CSSTransition } from 'react-transition-group'
import { Link } from 'gatsby'

import * as spacing from 'styles/spacing'
import { white, black, borderColor } from 'styles/colors'
import { animationTime, animationCurve, navHeight, boxShadow } from 'styles/global'
import { fontSizes, fontWeights } from 'styles/typography'
import { media } from 'styles/media'

import NavLabel from '../NavLabel'

import './styles.css'

const Container = styled.div`
  position: static;

  & + & {
    margin-left: ${spacing.xLarge};

    ${media.large`
      margin-left: ${spacing.xxLarge};
    `};

    ${media.xLarge`
      margin-left: ${spacing.xxxLarge};
    `};
  }
`

const Trigger = styled.button`
  display: flex;
  align-items: center;
  height: ${navHeight};
  transition: color ${animationTime} ${animationCurve};
`

const Dropdown = styled.div`
  position: absolute;
  top: ${navHeight};
  left: 0;
  width: 100%;
  background-color: ${white};
  transition: opacity ${animationTime} ${animationCurve}, visibility ${animationTime} ${animationCurve};
  z-index: 10;
  ${boxShadow};
`

const Outer = styled.div`
  border-top: 1px solid ${borderColor};
  position: relative;
  width: 100%;
  padding: ${spacing.large} ${spacing.xxLarge};

  ${media.large`
    padding: ${spacing.xLarge} 0;
    padding-left: 214px;
    padding-right: 100px;
  `};

  ${media.xLarge`
    padding: ${spacing.xxLarge} 0;
    padding-left: 240px;
    padding-right: 150px;
  `};
`

const Inner = styled.div`
  display: flex;
  align-items: flex-start;
  margin-left: -${spacing.medium};
  margin-right: -${spacing.medium};
`

const Group = styled.div`
  width: 33.3333%;
  text-align: left;
  padding: 0 ${spacing.medium};

  ${props =>
    props.flexible &&
    css`
      width: auto;
    `};
`

const GroupInner = styled.div`
  display: flex;
`

const List = styled.div`
  width: 100%;
`

const FigureWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${spacing.medium};
  border-right: 1px solid ${borderColor};
  padding-right: ${spacing.large};
  flex-shrink: 0;
  height: 72px;

  ${props =>
    props.pullLeft &&
    css`
      margin-left: -${spacing.xxLarge};
    `};
`

const Figure = styled.img`
  position: static;
`

const Title = styled.span`
  display: block;
  font-weight: ${fontWeights.medium};
  color: ${black};
  margin-bottom: ${spacing.medium};

  ${media.large`
    margin-bottom: ${spacing.large};
  `};
`

const StyledLink = styled(Link)`
  display: block;
  font-size: ${fontSizes.small};

  & + & {
    margin-top: ${spacing.small};
  }
`

const MegaMenu = ({ title, transparent, items, hoverFunc, hoverState }) => {
  return (
    <Container
      onMouseLeave={() => {
        hoverFunc(null)
      }}
      onMouseEnter={() => {
        hoverFunc(title)
      }}
    >
      <Trigger>
        <NavLabel data-tracking={`nav--link--text--${title}`} transparent={transparent}>
          {title}
        </NavLabel>
      </Trigger>
      <CSSTransition in={hoverState} timeout={500} classNames="menu" unmountOnExit>
        <Dropdown>
          <Outer>
            <Inner>
              {items.map((item, i) => (
                <Group
                  key={i}
                  css={`
                    flex: ${item.weight};
                  `}
                  flexible={item.weight}
                >
                  <Title>{item.title}</Title>
                  <GroupInner>
                    {item.figure && (
                      <FigureWrap pullLeft={item.figure === 'vuse-x-you'}>
                        <Figure
                          width={item.figureWidth}
                          src={`${process.env.BASE_PATH}/images/menu/${item.figure}.png`}
                        />
                      </FigureWrap>
                    )}
                    <List>
                      {item.links.map((link, i, ...rest) => {
                        const isExternal = link.link.startsWith('http')

                        const getElement = () => {
                          if (isExternal) return 'a'
                          return Link
                        }

                        const getLinkProps = () => {
                          if (isExternal) return { href: link.link }
                          return { to: link.link }
                        }

                        return (
                          <StyledLink
                            data-tracking={`nav--link--text--${link.label}`}
                            key={i}
                            as={getElement(rest)}
                            {...getLinkProps()}
                          >
                            {link.label}
                          </StyledLink>
                        )
                      })}
                    </List>
                  </GroupInner>
                </Group>
              ))}
            </Inner>
          </Outer>
        </Dropdown>
      </CSSTransition>
    </Container>
  )
}

MegaMenu.propTypes = {
  hoverFunc: func.isRequired,
  hoverState: bool
}

export default MegaMenu
